import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LIVESTOCK_SUMMARY_INDEX } from '../../../router/routes';
import { formatDateValue, route } from '../../../services/utils';
import { LinkComponent } from '../../../components/form/elements/link';
import { faLongArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import css from './summary-view.module.css';
import { useTranslation } from 'react-i18next';
import { useService } from '../../../services/services';
import SomaticCountSummary from './components/somatic/somatic-count-summary';
import InfectedCowSummary from './components/somatic/infected-cow-summary';
import ChronicDiseaseSummary from './components/somatic/chronic-disease-summary';
import NewDiseaseLactationSummary from './components/somatic/new-disease-lactation-summary';
import InfectedCalvingSummary from './components/somatic/infected-calving-summary';
import NewDiseaseLabourSummary from './components/somatic/new-disease-labour-summary';
import CuredCowSummary from './components/somatic/cured-cow-summary';
import { SomaticCellsInfo } from '../../../interfaces/livestock-somatic-cells';

export enum TabKey {
  DAIRY = 'dairy',
}

export default function ViewSomaticCells() {
  const { livestockSomaticCellsService, livestockSummaryService } = useService();
  const { t } = useTranslation();
  const { farmId, id } = useParams<{ farmId: string; id: string }>();

  const SummaryType = {
    SUMMARY_SOMATIC_CELLS_COUNT: t('pages.livestock.summary.header.summary_somatic_cells_count'),
    SUMMARY_INFECTED_COW: t('pages.livestock.summary.header.summary_infected_cow'),
    SUMMARY_INFECTED_COW_AFTER_CALVING: t('pages.livestock.summary.header.summary_infected_cow_after_calving'),
    SUMMARY_CHRONIC_DISEASED_COW: t('pages.livestock.summary.header.summary_chronic_diseased_cow'),
    SUMMARY_NEW_DISEASE_LACTATION_PERIOD: t('pages.livestock.summary.header.summary_new_disease_lactation_period'),
    SUMMARY_NEW_DISEASE_LABOUR_PERIOD: t('pages.livestock.summary.header.summary_new_disease_labour_period'),
    SUMMARY_CURED_COW_LABOUR_PERIOD: t('pages.livestock.summary.header.summary_cured_cows'),
  };

  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [inputValue, setInputValue] = useState<number>(0);
  const [somaticCellsData, setSomaticCellsData] = useState<SomaticCellsInfo>({
    averageCellsCount: [],
    averageCellsCountInYear: null,
    infectedCow: [],
    chronicDiseasedCow: [],
    newDiseaseLactationPeriod: [],
    newDiseaseAfterCalving: [],
    newDiseaseLabourPeriod: [],
    curedCowAfterDisease: [],
  });

  useEffect(() => {
    livestockSomaticCellsService.summaryType(farmId, id).subscribe(
      (response: any) => {
        setTitle(response.summary);
        setDate(response.current_date);

        livestockSummaryService.getConfigurations(farmId, encodeURIComponent(response.summary)).subscribe(config => {
          setInputValue(config);
        });
      })
  }, [livestockSomaticCellsService, livestockSummaryService, farmId, id]);

  useEffect(() => {
    livestockSomaticCellsService.summaryInfo(farmId, date, title).subscribe(
      (response: SomaticCellsInfo) => {
        setSomaticCellsData(response);
      })
  }, [livestockSomaticCellsService, farmId, date, title]);

  const handleInputChange = (event: any) => {
    const value = event.target.value.replace(/\D/g, '');
    setInputValue(value);

    livestockSummaryService.saveConfigurations(farmId, encodeURIComponent(title), value).subscribe();
  };

  return (
    <div className={css['field-info']}>
      <div className={css['header']}>
        <LinkComponent
          label="back"
          icon={faLongArrowLeft}
          linkTo={route(LIVESTOCK_SUMMARY_INDEX, {
            farmId: farmId,
            tab: TabKey.DAIRY,
          })}
        />
        <h2>{title ? title : ''}</h2>
      </div>

      <div className={css['top-bar']}>
        <div>
          <span>{t('pages.livestock.summary.section.control_date')}</span>
          <span className={css['strong']}>: {date && formatDateValue(date)} </span>
          <p>
            {title === SummaryType.SUMMARY_SOMATIC_CELLS_COUNT ? t('pages.livestock.summary.view.somatic_cells_count_goal') : t('pages.livestock.summary.view.percentage_goal')}
            <input
              className={css['goal-picker']}
              type="text"
              value={inputValue}
              onInput={handleInputChange}
            />
          </p>
        </div>
      </div>

      {title === SummaryType.SUMMARY_SOMATIC_CELLS_COUNT && (
        <SomaticCountSummary somaticCellsData={somaticCellsData} title={title} inputValue={inputValue} />
      )}

      {title === SummaryType.SUMMARY_INFECTED_COW && (
        <InfectedCowSummary somaticCellsData={somaticCellsData} title={title} inputValue={inputValue} />
      )}

      {title === SummaryType.SUMMARY_CHRONIC_DISEASED_COW && (
        <ChronicDiseaseSummary somaticCellsData={somaticCellsData} title={title} inputValue={inputValue} />
      )}

      {title === SummaryType.SUMMARY_NEW_DISEASE_LACTATION_PERIOD && (
        <NewDiseaseLactationSummary somaticCellsData={somaticCellsData} title={title} inputValue={inputValue} />
      )}

      {title === SummaryType.SUMMARY_INFECTED_COW_AFTER_CALVING && (
        <InfectedCalvingSummary somaticCellsData={somaticCellsData} title={title} inputValue={inputValue} />
      )}

      {title === SummaryType.SUMMARY_NEW_DISEASE_LABOUR_PERIOD && (
        <NewDiseaseLabourSummary somaticCellsData={somaticCellsData} title={title} inputValue={inputValue} />
      )}

      {title === SummaryType.SUMMARY_CURED_COW_LABOUR_PERIOD && (
        <CuredCowSummary somaticCellsData={somaticCellsData} title={title} inputValue={inputValue} />
      )}
    </div>
  );
}